import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ClipboardList, Activity, CheckCircle2, UserPlus, KeyRound } from "lucide-react";
import { api, CONTENT_TYPES, isManager } from "../lib/api";
import { useRoles } from "../context/RolesContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../components/ui-kit";

const Kpi = ({ label, value, icon: Icon, to, id, accent }) => (
  <Link to={to} data-testid={`kpi-${id}`} className="group block">
    <Panel className="p-5 transition-colors duration-200 group-hover:border-[#FF6B00]/50">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-[#FF6B00]" : "text-slate-600"}`} />
      </div>
      <div className="mt-3 font-heading text-4xl font-bold" data-testid={`kpi-${id}-value`}>{value}</div>
    </Panel>
  </Link>
);

const Bars = ({ title, data, labels, colorOf }) => {
  const max = Math.max(1, ...Object.values(data));
  return (
    <Panel className="p-6">
      <div className="mb-5 font-heading text-lg font-semibold">{title}</div>
      <div className="space-y-3">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="grid grid-cols-[130px_1fr_32px] items-center gap-3 text-sm">
            <span className="truncate text-slate-400" title={labels[k]?.label || k}>{labels[k]?.label || k}</span>
            <div className="h-2 overflow-hidden rounded-full bg-[#0B0D12]">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(v / max) * 100}%`, background: colorOf(k) }} />
            </div>
            <span className="text-right font-mono text-xs">{v}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { roleMap } = useRoles();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="text-rose-300" data-testid="dashboard-error">Couldn't load stats. Refresh to try again.</div>;
  if (!s) return <div className="text-slate-500">Loading…</div>;
  return (
    <div>
      <PageHeader eyebrow={new Date().toDateString()} title={`Hello, ${user.name.split(" ")[0]}`} desc="Here's what's moving through the Mathsmania pipeline today." />
      {user.is_admin && s.pending_users > 0 && (
        <Link to="/users" data-testid="pending-users-alert" className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-sm text-amber-200 transition-colors hover:bg-amber-500/15">
          <UserPlus className="h-4 w-4" /> {s.pending_users} registration{s.pending_users > 1 ? "s" : ""} waiting for your approval
        </Link>
      )}
      {isManager(user) && s.pending_password_requests > 0 && (
        <Link to="/password-requests" data-testid="pending-password-requests-alert" className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-sm text-amber-200 transition-colors hover:bg-amber-500/15">
          <KeyRound className="h-4 w-4" /> {s.pending_password_requests} password change request{s.pending_password_requests > 1 ? "s" : ""} waiting for approval
        </Link>
      )}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi id="my-queue" label="In your queue" value={s.my_queue} icon={Inbox} to="/queue" accent />
        <Kpi id="my-active" label="You're working on" value={s.my_active} icon={ClipboardList} to="/my-tasks" />
        <Kpi id="in-progress" label="In progress (all)" value={s.in_progress} icon={Activity} to={isManager(user) ? "/tasks" : "/queue"} />
        <Kpi id="completed" label="Completed (all)" value={s.completed} icon={CheckCircle2} to={isManager(user) ? "/tasks" : "/done"} />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Bars title="Waiting in queue, by role" data={s.queued_by_role} labels={roleMap} colorOf={(k) => roleMap[k]?.color || "#FF6B00"} />
        <Bars title="Active work, by content type" data={s.active_by_type} labels={CONTENT_TYPES} colorOf={(k) => CONTENT_TYPES[k].color} />
      </div>
    </div>
  );
}
